import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Mail, AlertTriangle, CheckCircle } from 'react-feather';
import Button from '../../components/common/Button';
import Card from '../../components/common/Card';
import api from '../../services/api';
import { toast } from 'react-toastify';

const VerificationRequired: React.FC = () => {
  const [email, setEmail] = useState<string>('');
  const [isResending, setIsResending] = useState<boolean>(false);
  const [resendSuccess, setResendSuccess] = useState<boolean>(false);
  
  // Get email from localStorage
  useEffect(() => {
    const storedEmail = localStorage.getItem('pendingVerificationEmail');
    if (storedEmail) {
      setEmail(storedEmail);
    }
  }, []);
  
  const handleResendEmail = async () => {
    if (!email) {
      toast.error('Email address not found. Please try logging in again.');
      return;
    }
    
    setIsResending(true);
    setResendSuccess(false);
    
    try {
      await api.post('/auth/resend-verification', { email });
      setResendSuccess(true);
      toast.success('Verification email resent! Please check your inbox.');
    } catch (err: any) {
      console.error('Failed to resend verification email:', err);
      toast.error(err.response?.data?.message || 'Failed to resend verification email');
    } finally {
      setIsResending(false);
    }
  };
  
  return (
    <div className="min-h-screen bg-gray-100 flex flex-col justify-center py-12 sm:px-6 lg:px-8">
      <div className="sm:mx-auto sm:w-full sm:max-w-md">
        <div className="flex justify-center">
          <AlertTriangle className="h-12 w-12 text-yellow-500" />
        </div>
        <h2 className="mt-6 text-center text-3xl font-extrabold text-gray-900">
          Verify Your Email
        </h2>
        <p className="mt-2 text-center text-sm text-gray-600">
          Please verify your email address to continue
        </p>
      </div>

      <div className="mt-8 sm:mx-auto sm:w-full sm:max-w-md">
        <Card>
          <div className="px-4 py-5 sm:p-6">
            <div className="flex items-center justify-center w-12 h-12 mx-auto bg-yellow-100 rounded-full">
              <Mail className="w-6 h-6 text-yellow-600" />
            </div>
            
            <div className="mt-3 text-center sm:mt-5">
              <h3 className="text-lg font-medium text-gray-900">
                Email Verification Required
              </h3>
              <div className="mt-2">
                {email ? (
                  <p className="text-sm text-gray-500">
                    We've sent a verification email to <span className="font-medium">{email}</span>.
                    Please check your inbox and click the verification link to activate your account.
                  </p>
                ) : (
                  <p className="text-sm text-red-500">
                    Unable to determine your email address. Please try logging in again.
                  </p>
                )}
              </div>
            </div>
            
            {resendSuccess && (
              <div className="mt-4 p-3 bg-green-50 rounded-md flex items-start">
                <CheckCircle className="h-5 w-5 text-green-500 mt-0.5 mr-2" />
                <p className="text-sm text-green-700">
                  Verification email resent successfully! Please check your inbox and spam folder.
                </p>
              </div>
            )}
            
            <div className="mt-5 sm:mt-6 space-y-3">
              <Button
                onClick={handleResendEmail}
                loading={isResending}
                disabled={isResending || !email}
                fullWidth
              >
                Resend Verification Email
              </Button>
              
              <p className="text-sm text-gray-500 text-center">
                Already verified? <Link to="/login" className="font-medium text-pink-600 hover:text-pink-500">
                  Try logging in again
                </Link>
              </p>
              
              <div className="text-sm text-gray-500 mt-5 text-center">
                <p>Need help? <a href="mailto:support@wedplanner.com" className="font-medium text-pink-600 hover:text-pink-500">
                  Contact support
                </a></p>
              </div>
            </div>
          </div>
        </Card>
      </div>
    </div>
  );
};

export default VerificationRequired;